// @ts-nocheck
import styled from 'styled-components';
import React, { ComponentType } from 'react';

import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import pickBy from 'lodash/pickBy';
import isFunction from 'lodash/isFunction';
import fontSizeStyles from './FontSize/FontSize.style';

import spacingStyles from './Spacing/Spacing.style';

import sizingStyles from './Sizing/Sizing.style';

import colorStyles from './Color/Color.style';

import displayStyles from './Display/Display.style';

import flexBoxStyles from './FlexBox/FlexBox.style';

const baseStyleProps = ['color', 'bgColor', 'display', 'fontSize', 'width', 'height'];

export interface BreakpointType {
  sm?: number | string;
  md?: number | string;
  lg?: number | string;
  xlg?: number | string;
  xxlg?: number | string;
}

const spacingStylesProps = [
  'p',
  'px',
  'py',
  'pl',
  'pr',
  'pt',
  'pb',
  'm',
  'mx',
  'my',
  'ml',
  'mr',
  'mt',
  'mb',
];

const flexBoxStylesProps = [
  'flexDirection',
  'flexWrap',
  'justifyContent',
  'alignItems',
  'alignContent',
  'order',
  'flexGrow',
  'flexShrink',
  'alignSelf',
];

interface ColorProps {
  bgColor?: string;
  color?: string;
}

interface DisplayProps {
  display?: string | BreakpointType;
}

export interface FlexProps {
  flexDirection?: string | BreakpointType;
  flexWrap?: string | BreakpointType;
  justifyContent?: string | BreakpointType;
  alignItems?: string | BreakpointType;
  alignContent?: string | BreakpointType;
  order?: string | number | BreakpointType;
  flexGrow?: string | BreakpointType;
  flexShrink?: string | BreakpointType;
  alignSelf?: string | BreakpointType;
  children?: React.ReactNode;
  as?: string | ComponentType;
  className?: string;
}

interface FontSizeProps {
  fontSize?: 'small' | 'xsmall' | 'regular' | 'large' | string | number | BreakpointType;
}

interface SizingProps {
  width?: string | number | BreakpointType;
  height?: string | number | BreakpointType;
}

export interface SpacingProps {
  p?: string | number | Object;
  px?: string | number | Object;
  py?: string | number | Object;
  pl?: string | number | Object;
  pr?: string | number | Object;
  pt?: string | number | Object;
  pb?: string | number | Object;
  m?: string | number | Object;
  mx?: string | number | Object;
  my?: string | number | Object;
  ml?: string | number | Object;
  mr?: string | number | Object;
  mt?: string | number | Object;
  mb?: string | number | Object;
  children?: React.ReactNode;
  as?: string | ComponentType;
  className?: string;
}

export interface WithStylesProps
  extends DisplayProps,
    FontSizeProps,
    ColorProps,
    SizingProps,
    SpacingProps,
    FlexProps {
  as?: string | ComponentType;
  className?: string;
  theme?: any;
}

const withStyles = <P extends object>(
  Component: ComponentType<P> | keyof React.JSX.IntrinsicElements,
  componentStyles?: any = '',
  options: { attrs?: any; flexbox?: boolean; spacing?: boolean } = {}
): ComponentType<P & WithStylesProps> => {
  const { attrs, flexbox = false, spacing = false } = options;

  let stylesProps = baseStyleProps;
  /* istanbul ignore else */
  if (flexbox) {
    stylesProps = stylesProps.concat(flexBoxStylesProps);
  }
  /* istanbul ignore else */
  if (spacing) {
    stylesProps = stylesProps.concat(spacingStylesProps);
  }

  // Filter-out hoc props from child component
  const StyledComponent = styled(Component)
    .withConfig({ shouldForwardProp: (prop: any) => !stylesProps.includes(prop) })
    .attrs((props: any) => {
      const { defaultProps } = Component;
      const attrsValue = isFunction(attrs) ? attrs(props) : attrs;
      if (isEmpty(defaultProps)) {
        return attrsValue;
      }
      const extraProps = pickBy(defaultProps, (v, k) => isNil(props[k]));
      return { ...extraProps, ...attrsValue };
    })`
    ${componentStyles}
    ${fontSizeStyles}
    ${spacing && spacingStyles}
    ${colorStyles}
    ${sizingStyles}
    ${displayStyles}
    ${flexbox && flexBoxStyles}
  `;

  /* istanbul ignore next */
  StyledComponent.displayName = Component.displayName || Component.name || 'Component';

  /* istanbul ignore next */
  // eslint-disable-next-line no-param-reassign
  Component.displayName = `Base(${Component.displayName || Component.name || 'Component'})`;

  return StyledComponent;
};

export const withSpacingStyles = <P extends object>(
  Component: ComponentType<P> | keyof React.JSX.IntrinsicElements
): ComponentType<P & SpacingProps> => {
  return styled(Component).withConfig({
    shouldForwardProp: (prop: any) => !spacingStylesProps.includes(prop),
  })`
    ${spacingStyles}
  `;
};

export const withFlexBoxStyles = <P extends object>(
  Component: ComponentType<P> | keyof React.JSX.IntrinsicElements
): ComponentType<P & FlexProps> => {
  return styled(Component).withConfig({
    shouldForwardProp: (prop: any) => !flexBoxStylesProps.includes(prop),
  })`
    ${flexBoxStyles}
  `;
};

export default withStyles;
